import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatFechaDeMarca } from '../utils/format';
import { Check, Download, FileSearch, FileSpreadsheet, Trash2 } from 'lucide-react';
import BotonAyuda from '../components/BotonAyuda';

const MENSAJES_QB = {
  conectado: { tipo: 'ok', texto: 'QuickBooks conectado correctamente.' },
  error: { tipo: 'error', texto: 'No se pudo completar la conexión con QuickBooks. Intentá de nuevo.' },
  cancelado: { tipo: 'error', texto: 'Cancelaste la conexión con QuickBooks.' }
};

export default function Configuracion() {
  const { empresaActiva } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [usuarios, setUsuarios] = useState([]);
  const [solicitudes, setSolicitudes] = useState([]);
  const [nuevoEmail, setNuevoEmail] = useState('');
  const [nuevoRol, setNuevoRol] = useState('operador');
  const [mensaje, setMensaje] = useState('');
  const [rolPorSolicitud, setRolPorSolicitud] = useState({});

  const [estadoQB, setEstadoQB] = useState(null);
  const [migrandoQB, setMigrandoQB] = useState(null);
  const [resultadosQB, setResultadosQB] = useState({});
  const [errorQB, setErrorQB] = useState('');
  const [comparandoQB, setComparandoQB] = useState(false);
  const [informeQB, setInformeQB] = useState(null);
  const [bajandoAbiertas, setBajandoAbiertas] = useState(false);

  async function cargar() {
    // Cada pedido por separado: si falla uno (p. ej. usuarios), el estado de
    // QuickBooks igual se muestra con sus botones.
    api.get('/quickbooks/estado').then(({ data }) => setEstadoQB(data)).catch(() => setErrorQB('No se pudo leer el estado de QuickBooks; recarga la página.'));
    api.get('/usuarios').then(({ data }) => setUsuarios(data)).catch(() => {});
    api.get('/usuarios/solicitudes').then(({ data }) => setSolicitudes(data)).catch(() => {});
  }

  useEffect(() => {
    if (empresaActiva) {
      // Limpiar resultados/errores de QuickBooks de la empresa anterior — si no,
      // quedaban pegados en pantalla al cambiar de empresa aunque ya no correspondan.
      setResultadosQB({});
      setErrorQB('');
      setInformeQB(null);
      cargar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva]);

  const [avisoQB, setAvisoQB] = useState(null);

  useEffect(() => {
    const qb = searchParams.get('qb');
    if (qb && MENSAJES_QB[qb]) {
      setAvisoQB(MENSAJES_QB[qb]);
      searchParams.delete('qb');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function conectarQuickBooks() {
    window.location.href = `/api/quickbooks/conectar?empresa_id=${empresaActiva}`;
  }

  async function desconectarQuickBooks() {
    if (!window.confirm('¿Desconectar QuickBooks de esta empresa?')) return;
    try {
      await api.delete('/quickbooks/conexion');
      setResultadosQB({});
      cargar();
    } catch (err) {
      setErrorQB(err.response?.data?.error || 'No se pudo desconectar');
    }
  }

  const ACCIONES_QB = {
    categorias: { endpoint: 'sincronizar-categorias', label: 'Sincronizar categorías', formatear: (d) => `${d.creadas} categoría(s) nueva(s), ${d.actualizadas} actualizada(s) de ${d.total}.` },
    clientes: { endpoint: 'migrar-clientes', label: 'Migrar clientes', formatear: (d) => `${d.creados} cliente(s) nuevo(s), ${d.actualizados} actualizado(s) de ${d.total}.` },
    proveedores: { endpoint: 'migrar-proveedores', label: 'Migrar proveedores', formatear: (d) => `${d.creados} proveedor(es) nuevo(s), ${d.actualizados} actualizado(s) de ${d.total}.` },
    productos: { endpoint: 'migrar-productos', label: 'Migrar productos', formatear: (d) => `${d.creados} producto(s) nuevo(s), ${d.existentes} ya existían de ${d.total}.` }
  };

  async function ejecutarAccionQB(clave) {
    setMigrandoQB(clave);
    setErrorQB('');
    try {
      const { data } = await api.post(`/quickbooks/${ACCIONES_QB[clave].endpoint}`);
      setResultadosQB({ ...resultadosQB, [clave]: ACCIONES_QB[clave].formatear(data) });
    } catch (err) {
      setErrorQB(err.response?.data?.error || 'No se pudo completar la acción');
    } finally {
      setMigrandoQB(null);
    }
  }

  // Solo lee QuickBooks y el sistema: no crea ni cambia nada en ninguno de los dos.
  async function compararQB() {
    setComparandoQB(true);
    setErrorQB('');
    try {
      const { data } = await api.get('/quickbooks/comparar', { timeout: 0 });
      setInformeQB(data);
    } catch (err) {
      setErrorQB(err.response?.data?.error || 'No se pudo comparar con QuickBooks');
    } finally {
      setComparandoQB(false);
    }
  }

  function descargarInformeQB() {
    const url = URL.createObjectURL(new Blob([informeQB.texto], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `comparacion-quickbooks-${informeQB.generado_en.slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Excel de las facturas de QuickBooks a migrar para marcar la moneda de
  // cada una antes de traerlas. Solo lee.
  async function descargarAbiertasQB() {
    setBajandoAbiertas(true);
    setErrorQB('');
    try {
      const res = await api.get('/quickbooks/facturas-excel', { responseType: 'blob', timeout: 0 });
      const nombre = /filename="([^"]+)"/.exec(res.headers['content-disposition'] || '')?.[1] || 'facturas-quickbooks-2026.xlsx';
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = nombre;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      let mensaje = 'No se pudo descargar el Excel de QuickBooks';
      try { mensaje = JSON.parse(await err.response.data.text()).error || mensaje; } catch { /* sin detalle */ }
      setErrorQB(mensaje);
    } finally {
      setBajandoAbiertas(false);
    }
  }

  async function asignar(e) {
    e.preventDefault();
    setMensaje('');
    try {
      const { data } = await api.post('/usuarios/asignar-rol', { email: nuevoEmail, rol: nuevoRol });
      setMensaje(
        data.invitado
          ? `Invitación creada para ${nuevoEmail}. Cuando inicie sesión con Google, ya va a tener acceso directo.`
          : 'Rol asignado correctamente.'
      );
      setNuevoEmail('');
      cargar();
    } catch (err) {
      setMensaje(err.response?.data?.error || 'No se pudo asignar el rol.');
    }
  }

  async function aprobarSolicitud(email) {
    const rol = rolPorSolicitud[email] || 'operador';
    try {
      await api.post('/usuarios/asignar-rol', { email, rol });
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo aprobar la solicitud.');
    }
  }

  async function rechazarSolicitud(s) {
    if (!window.confirm(`¿Rechazar la solicitud de ${s.email}? Si vuelve a iniciar sesión, aparecerá de nuevo como solicitud.`)) return;
    try {
      await api.delete(`/usuarios/solicitudes/${s.id}`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo rechazar la solicitud.');
    }
  }

  async function revocar(usuarioId) {
    if (!window.confirm('¿Revocar el acceso de este usuario a la empresa?')) return;
    await api.delete(`/usuarios/${usuarioId}/acceso`);
    cargar();
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-800">Configuración<BotonAyuda /></h1>

      <div className="bg-white rounded-xl shadow-sm p-5">
        <h2 className="font-semibold text-slate-700 mb-1">Integración QuickBooks</h2>
        <p className="text-xs text-slate-400 mb-3">
          Migración única desde QuickBooks: plan de cuentas (como categorías de movimientos), clientes, proveedores y productos, en vez de cargarlos a mano.
        </p>

        {avisoQB && (
          <p className={`text-sm mb-3 rounded-md p-2 ${avisoQB.tipo === 'ok' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            {avisoQB.texto}
          </p>
        )}

        {estadoQB?.conectado ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Conectado a la compañía QuickBooks{' '}
              <span className="font-medium">{estadoQB.nombre_compania || '(nombre no disponible)'}</span>
              {' '}— confirmá que sea la correcta para esta empresa antes de migrar datos.
            </p>
            <p className="text-sm text-slate-500">
              Desde {formatFechaDeMarca(estadoQB.conectado_en)} · ambiente <span className="font-medium">{estadoQB.ambiente}</span>
            </p>
            <div className="border border-slate-200 rounded-md p-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={compararQB}
                  disabled={comparandoQB || migrandoQB !== null}
                  className="inline-flex items-center gap-1.5 bg-white border border-primary-600 text-primary-700 px-4 py-2 rounded-md text-sm hover:bg-primary-50 disabled:opacity-50"
                >
                  <FileSearch size={16} />
                  {comparandoQB ? 'Comparando… (puede tardar un par de minutos)' : 'Comparar con QuickBooks'}
                </button>
                <button
                  onClick={descargarAbiertasQB}
                  disabled={bajandoAbiertas || comparandoQB || migrandoQB !== null}
                  className="inline-flex items-center gap-1.5 bg-white border border-primary-600 text-primary-700 px-4 py-2 rounded-md text-sm hover:bg-primary-50 disabled:opacity-50"
                >
                  <FileSpreadsheet size={16} />
                  {bajandoAbiertas ? 'Preparando Excel… (puede tardar)' : 'Facturas 2026 (Excel)'}
                </button>
                {informeQB && (
                  <button
                    onClick={descargarInformeQB}
                    className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-primary-700"
                    title="Descargar el informe"
                  >
                    <Download size={16} /> Descargar
                  </button>
                )}
              </div>
              <p className="text-xs text-slate-400">
                Solo leen, no cambian nada en QuickBooks ni en el sistema. Comparar muestra qué falta o ya existe; el Excel trae las facturas de QuickBooks a migrar (las de 2026, las anteriores pagadas en 2026 y las abiertas) para marcar en cada una si era BCV, USD o BS.
              </p>
              {informeQB && (
                <pre className="text-xs bg-slate-50 rounded-md p-3 max-h-96 overflow-auto whitespace-pre-wrap">{informeQB.texto}</pre>
              )}
            </div>
            <div className="space-y-2">
              {Object.entries(ACCIONES_QB).map(([clave, accion]) => (
                <div key={clave} className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => ejecutarAccionQB(clave)}
                    disabled={migrandoQB !== null}
                    className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm hover:bg-primary-700 disabled:opacity-50 min-w-[180px]"
                  >
                    {migrandoQB === clave ? 'Procesando...' : accion.label}
                  </button>
                  {resultadosQB[clave] && <p className="text-sm text-green-700">{resultadosQB[clave]}</p>}
                </div>
              ))}
            </div>
            <button
              onClick={desconectarQuickBooks}
              className="bg-white border border-slate-300 text-slate-600 px-4 py-2 rounded-md text-sm hover:bg-slate-50"
            >
              Desconectar
            </button>
          </div>
        ) : (
          <button
            onClick={conectarQuickBooks}
            className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm hover:bg-primary-700"
          >
            Conectar con QuickBooks
          </button>
        )}
        {errorQB && <p className="text-red-600 text-sm mt-2">{errorQB}</p>}
      </div>

      {solicitudes.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <h2 className="font-semibold text-amber-800 mb-1">
            Solicitudes de acceso pendientes ({solicitudes.length})
          </h2>
          <p className="text-xs text-amber-700 mb-3">
            Estas personas ya intentaron iniciar sesión con Google pero todavía no tienen ningún rol asignado.
          </p>
          <div className="space-y-2">
            {solicitudes.map((s) => (
              <div key={s.id} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between bg-white rounded-lg p-3">
                <div className="flex items-center gap-3">
                  {s.foto_url && <img src={s.foto_url} alt="" className="w-8 h-8 rounded-full" referrerPolicy="no-referrer" />}
                  <div>
                    <p className="text-sm font-medium text-slate-700">{s.nombre}</p>
                    <p className="text-xs text-slate-400">{s.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={rolPorSolicitud[s.email] || 'operador'}
                    onChange={(e) => setRolPorSolicitud({ ...rolPorSolicitud, [s.email]: e.target.value })}
                    className="border rounded-md px-2 py-1 text-sm"
                  >
                    <option value="gerente">Gerente</option>
                    <option value="contador">Contador</option>
                    <option value="operador">Operador</option>
                    <option value="consultor">Consultor</option>
                  </select>
                  <button
                    onClick={() => aprobarSolicitud(s.email)}
                    title="Aprobar solicitud"
                    className="text-slate-400 hover:text-green-600 transition-colors"
                  >
                    <Check size={16} />
                  </button>
                  <button
                    onClick={() => rechazarSolicitud(s)}
                    title="Rechazar solicitud"
                    className="text-slate-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm p-5">
        <h2 className="font-semibold text-slate-700 mb-3">Invitar a un usuario</h2>
        <p className="text-xs text-slate-400 mb-3">
          No necesitas esperar a que inicie sesión primero — se le da acceso directo con solo su correo.
        </p>
        <form onSubmit={asignar} className="flex flex-wrap gap-3">
          <input
            required
            type="email"
            placeholder="correo@empresa.com"
            value={nuevoEmail}
            onChange={(e) => setNuevoEmail(e.target.value)}
            className="border rounded-md px-3 py-2 flex-1 min-w-[220px]"
          />
          <select value={nuevoRol} onChange={(e) => setNuevoRol(e.target.value)} className="border rounded-md px-3 py-2">
            <option value="gerente">Gerente</option>
            <option value="contador">Contador</option>
            <option value="operador">Operador</option>
            <option value="consultor">Consultor</option>
          </select>
          <button type="submit" className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700">
            Invitar
          </button>
        </form>
        {mensaje && <p className="text-sm mt-2 text-slate-600">{mensaje}</p>}
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="p-3">Usuario</th>
                <th className="p-3">Email</th>
                <th className="p-3">Rol</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map((u) => (
                <tr key={u.id} className="border-t hover:bg-slate-50 transition-colors">
                  <td className="p-3 whitespace-nowrap">{u.nombre}</td>
                  <td className="p-3 whitespace-nowrap">{u.email}</td>
                  <td className="p-3 capitalize whitespace-nowrap">{u.rol}</td>
                  <td className="p-3 whitespace-nowrap">
                    <button onClick={() => revocar(u.id)} className="text-red-500 hover:underline text-xs">
                      Revocar acceso
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
